/**
 * Group sizing and nesting flags — the code that must never feed the render loop.
 *
 * The canvas has crashed with effect_update_depth_exceeded three times, and every time the
 * circle was the same: a card re-renders, xyflow writes its measured size back into the
 * `nodes` array, and something re-derives from that array and re-renders the card. This
 * module exists to break both halves of that circle:
 *
 * - **Nesting facts travel ON the node** (`data.nested`, `data.childCount`), set here, where
 *   nesting actually changes — so a card derives only from its own props and never watches
 *   the array xyflow writes into.
 * - **A no-op is a no-op.** Unchanged nodes come back with their identity intact, and if
 *   every node is unchanged the ORIGINAL array comes back — assigning a fresh array to a
 *   bound prop re-renders the whole canvas even when the contents are identical, and at
 *   mount that render is exactly what feeds the measure→render circle.
 *
 * Pure and extracted so the no-op guarantee is TESTED rather than hoped for.
 */

/**
 * Size parent cards to hold their children, and stamp nesting facts onto every node.
 *
 * @param {array} list  flow nodes
 * @returns {array} the SAME array when nothing changed, else a new one with changed nodes replaced
 */
export function sizeParents(list) {
    const children = new Map();
    for (const node of list ?? []) {
        if (!node.parentId) continue;
        if (!children.has(node.parentId)) children.set(node.parentId, []);
        children.get(node.parentId).push(node);
    }

    const sized = (list ?? []).map((node) => {
        const kids = children.get(node.id);
        const nested = !!node.parentId;
        const same = (style, count) => node.style === style
            && (node.data?.childCount ?? 0) === count
            && ((node.data?.nested ?? false) === nested);

        if (!kids) {
            // No longer a parent — drop any size we forced on it earlier.
            return same(undefined, 0)
                ? node
                : { ...node, style: undefined, data: { ...node.data, childCount: 0, nested } };
        }

        const right = Math.max(...kids.map((k) => k.position.x + (k.measured?.width ?? 220)));
        const bottom = Math.max(...kids.map((k) => k.position.y + (k.measured?.height ?? 130)));

        // A MINIMUM, not a size. Setting width/height outright fought a parent the user had
        // resized by hand — it snapped back to whatever its children needed on the next
        // change. This way it can never be smaller than what it holds, and staying bigger is
        // the user's call.
        const style = `min-width:${Math.max(260, right + 16)}px;min-height:${Math.max(150, bottom + 16)}px`;

        return same(style, kids.length)
            ? node
            : { ...node, style, data: { ...node.data, childCount: kids.length, nested } };
    });

    return sized.every((node, index) => node === list[index]) ? list : sized;
}
